import { Button, Typography, useBreakpoint } from "@povio/ui";
import { cx } from "class-variance-authority";
import { useRouter } from "next/router";
import { useMemo } from "react";

import { LogoSmartIcon } from "@/assets/icons/navbar/LogoSmart";
import Notifications from "@/components/notifications/Notifications";
import { HamburgerMenu } from "@/components/shared/layout/navbar/HamburgerMenu";
import { TreatsButton } from "@/components/shared/layout/sidebar/TreatsButton";
import { RouteConfig } from "@/config/route.config";
import { AuthContext } from "@/data/auth/auth.context";
import { RoutingUtils } from "@/util/routing.utils";

interface NavbarAction {
  buttonVariant?: "primary" | "outlined" | "text";
  label: string;
  onClick: () => void;
  dataTestId?: string;
  section?: "right" | "profile";
  type?: "toggle" | "button";
  mobileOnly?: boolean;
  desktopOnly?: boolean;
  icon?: React.FC<React.SVGProps<SVGSVGElement>>;
  className?: string;
}

export const Navbar = () => {
  const router = useRouter();
  const isDesktop = useBreakpoint("xl");
  const isTablet = useBreakpoint("md");

  const { isLoggedIn, logout } = AuthContext.useAuth();
  const isInOnboarding = RoutingUtils.isPathInRoutes(router.pathname, ["Onboarding"]);
  const isInSigninOrSignup =
    RoutingUtils.isPathInRoutes(router.pathname, ["Signin"]) ||
    RoutingUtils.isPathInRoutes(router.pathname, ["Signup"]);

  const navbarActions: NavbarAction[] = useMemo(() => {
    const actions: NavbarAction[] = [];

    if (isInOnboarding) {
      actions.push({
        label: "Log Out",
        dataTestId: "logout-button",
        onClick: () => {
          logout();
        },
        section: "right",
      });
    }

    return actions;
  }, [isInOnboarding, logout]);

  const logoutAction = navbarActions.find((action) => action.label === "Log Out");

  if (!isDesktop) {
    return (
      <header
        className={cx(
          "z-20 flex h-18 items-center justify-between bg-elevation-fill-default-0 p-2 pl-4",
          (isInSigninOrSignup || isInOnboarding) && "bg-transparent",
        )}
      >
        <MobileNavbar
          title={RoutingUtils.getPageHeaderTitle(router.asPath)}
          isInOnboarding={isInOnboarding}
          isLoggedIn={isLoggedIn}
        />
      </header>
    );
  }

  return (
    <header
      className={cx(
        "z-35 flex h-18 items-center justify-between bg-elevation-fill-default-0 p-2 pl-4",
        (isInSigninOrSignup || isInOnboarding) && "bg-transparent",
      )}
    >
      <div className="flex w-full items-center justify-between">
        <div
          onClick={() => router.push(RouteConfig.Home)}
          className="cursor-pointer"
        >
          <LogoSmartIcon className="h-[56px] w-[140px]" />
        </div>

        {isTablet && (
          <div
            className="flex flex-row gap-4"
            data-testid="navbar-right-side-actions"
          >
            {navbarActions.map((action) => (
              <Button
                key={action.label}
                variant="outlined"
                size="s"
                color="secondary"
                onPress={action.onClick}
                data-testid={action.dataTestId}
                className="mr-2"
              >
                {action.label}
              </Button>
            ))}
          </div>
        )}
        {!isTablet && isInOnboarding && logoutAction && (
          <Button
            variant="outlined"
            color="secondary"
            size="s"
            onPress={logoutAction.onClick}
            data-testid={logoutAction.dataTestId}
            className="mr-2"
          >
            {logoutAction.label}
          </Button>
        )}
        {!isDesktop && !isInOnboarding && isLoggedIn && (
          <div className="flex w-full flex-1 items-center justify-end gap-2">
            <TreatsButton />
            <Notifications />
            <HamburgerMenu />
          </div>
        )}
      </div>
    </header>
  );
};

const MobileNavbar = ({
  title,
  isInOnboarding,
  isLoggedIn,
  children,
}: {
  title: string;
  isInOnboarding: boolean;
  isLoggedIn: boolean;
  children?: React.ReactNode;
}) => {
  const isTablet = useBreakpoint("md");

  return (
    <div className="flex w-full flex-row items-center justify-between gap-2">
      <div className="flex flex-row items-center gap-2">
        {!isInOnboarding && isLoggedIn && (
          <>
            <HamburgerMenu />
            <Typography
              size="title-4"
              variant="prominent-1"
              className="line-clamp-2 block t:hidden"
            >
              {title}
            </Typography>
          </>
        )}
      </div>
      {isTablet && children}

      <div className="flex flex-row items-center gap-2">
        {!isInOnboarding && isLoggedIn && (
          <>
            <TreatsButton />
            <Notifications />
          </>
        )}
        <LogoSmartIcon className="h-[38px] w-auto object-cover" />
      </div>
    </div>
  );
};
